// Theme event fired by assets/variant-picker.js when the shopper changes variant.
const VARIANT_UPDATE_EVENT = 'variant:update';

// Alternate collection template that renders this same section for a tag-filtered product list.
const SIZE_MATCH_VIEW = 'thermite-size-match';
const SIZE_MATCH_SECTION_KEY = 'thermite:size-match-section-id';

/**
 * Tire size patterns, most specific first.
 *
 * A single pattern used to miss 174 products: commercial sizes have no aspect ratio
 * (11R22.5, 8R19.5), so does old metric (185R14), and flotation section widths can be
 * one digit (29X9R14, 25X12R9). Those pages silently lost size matching altogether.
 */
const SIZE_PATTERNS = [
  // 275/55R20, 33X12.50R20, 29X9R14, 285/75R24.5
  /\d{2,3}(?:\.\d+)?\s*[\/xX]\s*\d{1,3}(?:\.\d+)?\s*[A-Za-z]{0,3}\s*\d{1,2}(?:\.\d+)?[A-Za-z]{0,2}/,
  // 11R22.5, 8R19.5, 185R14 - no aspect ratio at all
  /\d{1,3}(?:\.\d{1,2})?\s*[A-Za-z]{1,2}\s*\d{2}(?:\.\d)?/,
];

/**
 * Pull the tire size out of a variant option value.
 * Values look like "245/45R20", "245/45R20 - 88W XL BSW", "11R22.5 - BW".
 * @param {string} value
 * @returns {string | null}
 */
function extractSize(value) {
  if (!value) return null;
  for (const pattern of SIZE_PATTERNS) {
    const match = String(value).match(pattern);
    if (match) return match[0].replace(/\s+/g, '');
  }
  return null;
}

/**
 * Turn a tag into the handle Shopify uses in /collections/all/<handle> URLs.
 * @param {string} tag
 * @returns {string}
 */
function tagHandle(tag) {
  return tag
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // Ignore our own bookkeeping of the size currently on display
      if (mutation.attributeName === 'data-current-size') continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * The product JSON for the product being viewed, used to resolve a variant id to its size
   * @type {Promise<any> | null}
   */
  #productData = null;

  /** @type {(event: Event) => void} */
  #onVariantUpdate = (event) => {
    const variantId = /** @type {any} */ (event).detail?.resource?.id;
    if (!variantId) return;
    this.#loadRecommendations(String(variantId));
  };

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    if (this.#sizeMatchingEnabled) {
      document.addEventListener(VARIANT_UPDATE_EVENT, this.#onVariantUpdate);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(VARIANT_UPDATE_EVENT, this.#onVariantUpdate);
  }

  get #sizeMatchingEnabled() {
    return this.dataset.matchSize !== 'false' && Boolean(this.dataset.productHandle);
  }

  /**
   * Load the product recommendations
   * @param {string} [variantId] - the variant to size-match against, defaults to the one in the URL
   */
  #loadRecommendations(variantId) {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true' && !variantId) {
      return;
    }

    const defaultUrl = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    this.#resolveRecommendations(productId, sectionId, intent, variantId)
      .then(async (result) => {
        if (!result || !result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result ? result.status : 'no content'}`));
          }
          return;
        }

        let size = result.size || '';
        // The size-matched render comes from the collection template, so its section id differs.
        let recommendations = this.#extractSection(result.data, size ? null : id);

        if (size) {
          this.#pruneSlides(recommendations, size);

          // Pruning can empty the carousel: the only tire in this size was the one on
          // screen, or the rest are sold out in it. Fall back rather than show nothing.
          if (!recommendations?.querySelector('.resource-list__item')) {
            const fallback = await this.#fetchCached(defaultUrl);
            if (fallback.success) {
              recommendations = this.#extractSection(fallback.data, id);
              size = '';
            }
          }
        }

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.dataset.currentSize = size;
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Pull the section element out of a rendered response.
   * @param {string} data
   * @param {string | null} id - match this section id, or take the first one when null
   * @returns {Element | null}
   */
  #extractSection(data, id) {
    const html = document.createElement('div');
    html.innerHTML = data || '';
    return id
      ? html.querySelector(`product-recommendations[id="${id}"]`)
      : html.querySelector('product-recommendations');
  }

  /**
   * Fetch size-matched recommendations when we can work out the size on display, otherwise
   * fall back to Shopify's own related-products recommendations.
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @param {string} [variantId]
   * @returns {Promise<{ success: true, data: string, size?: string } | { success: false, status: number } | null>}
   */
  async #resolveRecommendations(productId, sectionId, intent, variantId) {
    if (this.#sizeMatchingEnabled) {
      try {
        const size = await this.#currentSize(variantId);

        if (size) {
          const result = await this.#fetchSizeMatched(size);

          // Only keep the size-matched render when it actually produced cards.
          if (result?.success && result.data.includes('resource-list__item')) {
            return { ...result, size };
          }
        }
      } catch {
        // Fall through to the default recommendations below.
      }
    }

    return this.#fetchCached(`${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`);
  }

  /**
   * Render this section against the tag-filtered /collections/all/<size> URL.
   *
   * The section id there belongs to the collection template, not this product template, so the
   * first call fetches the whole alternate-view page to learn it and every later call uses the
   * cheap section-rendering endpoint.
   * @param {string} size
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number } | null>}
   */
  async #fetchSizeMatched(size) {
    const base = `${Shopify.routes.root}collections/all/${tagHandle(size)}?view=${SIZE_MATCH_VIEW}&sort_by=best-selling`;
    const cachedSectionId = sessionStorage.getItem(SIZE_MATCH_SECTION_KEY);

    if (cachedSectionId) {
      const result = await this.#fetchCached(`${base}&section_id=${cachedSectionId}`);
      if (result.success) return result;
      sessionStorage.removeItem(SIZE_MATCH_SECTION_KEY);
    }

    const result = await this.#fetchCached(base);
    if (!result.success) return result;

    const found = new DOMParser().parseFromString(result.data, 'text/html').querySelector('product-recommendations');

    if (!found) return null;

    const sectionId = found.id.replace(/^product-recommendations-/, '');
    if (sectionId) sessionStorage.setItem(SIZE_MATCH_SECTION_KEY, sectionId);

    return { success: true, data: found.outerHTML };
  }

  /**
   * The tire size of the variant currently on display.
   * @param {string} [variantId]
   * @returns {Promise<string | null>}
   */
  async #currentSize(variantId) {
    const handle = this.dataset.productHandle;
    if (!handle) return null;

    if (!this.#productData) {
      this.#productData = fetch(`${Shopify.routes.root}products/${handle}.js`).then((response) => {
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        return response.json();
      });
    }

    const product = await this.#productData;
    const variants = product?.variants || [];
    const id = variantId || new URLSearchParams(window.location.search).get('variant');

    const variant =
      (id && variants.find((/** @type {any} */ item) => String(item.id) === String(id))) ||
      variants.find((/** @type {any} */ item) => item.available) ||
      variants[0];

    if (!variant) return null;

    const sizeIndex = (product.options || []).findIndex(
      (/** @type {any} */ option) => /size/i.test(typeof option === 'string' ? option : option.name)
    );

    const candidates = sizeIndex >= 0 ? [variant.options[sizeIndex]] : variant.options || [];
    for (const candidate of candidates) {
      const size = extractSize(candidate);
      if (size) return size;
    }

    return extractSize(variant.title);
  }

  /**
   * Does this card's product actually have the size in stock?
   *
   * The section stamps every card with the sizes it can currently sell; Liquid cannot know
   * which size the shopper is on, but we can.
   * @param {Element} slide
   * @param {string} size
   * @returns {boolean}
   */
  #stocksSize(slide, size) {
    const card = /** @type {HTMLElement | null} */ (slide.querySelector('[data-available-sizes]'));
    // A render without the attribute predates this check - leave it alone rather than blank the row.
    if (!card) return true;

    return (card.dataset.availableSizes || '')
      .split('|')
      .some((entry) => extractSize(entry) === size);
  }

  /**
   * Drop the card for the product the shopper is already looking at, drop anything sold out
   * in the size on screen, and renumber what is left so the carousel timeline stays in step.
   * @param {Element | null} recommendations
   * @param {string} size
   */
  #pruneSlides(recommendations, size) {
    const handle = this.dataset.productHandle;
    if (!recommendations || !handle) return;

    const max = Number(this.dataset.maxProducts) || 4;

    for (const scroller of recommendations.querySelectorAll('slideshow-slides, .resource-list--grid')) {
      for (const slide of Array.from(scroller.children)) {
        if (slide.querySelector(`a[href*="/products/${handle}"]`)) {
          slide.remove();
          continue;
        }
        if (size && !this.#stocksSize(slide, size)) slide.remove();
      }

      const remaining = Array.from(scroller.children);
      remaining.slice(max).forEach((slide) => slide.remove());

      remaining.slice(0, max).forEach((slide, index) => {
        if (slide.tagName.toLowerCase() !== 'slideshow-slide') return;
        slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
        slide.style.setProperty('view-timeline-name', `--slide-${index}`);
      });
    }
  }

  /**
   * Fetches a section render and caches the result for future use
   * @param {string} url
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCached(url) {
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
